/**
 * Копирование / вставка узлов на доске X6 с дублированием элементов модели ArchiMate
 * (новые elementRef + новые view nodes; связи между скопированными узлами — новые relationship).
 */
import * as renderView from './renderView';
import {NOTE} from '../util/ModelUtil';
import {isCanvasOnlyNodeType} from '../util/CanvasNodeTypes';

const CLIPBOARD_VERSION = 1;
const PASTE_OFFSET = 32;

function moddleColorToPatch(color) {
	if (!color || typeof color !== 'object') return undefined;
	const r = color.r ?? color.$attrs?.r;
	const g = color.g ?? color.$attrs?.g;
	const b = color.b ?? color.$attrs?.b;
	const a = color.a ?? color.$attrs?.a ?? 100;
	if (r == null || g == null || b == null) return undefined;
	return {r, g, b, a};
}

/**
 * Стили вида из view-элемента (как в nodeConfig / JsonSerializer).
 * @param {{ style?: object, textAlign?: string }} ve
 */
function captureViewStyleFields(ve) {
	const style = ve?.style;
	const textAlignRaw = style?.textAlign ?? style?.get?.('textAlign') ?? ve?.textAlign;
	const textAlign =
		textAlignRaw != null && String(textAlignRaw).trim() !== ''
			? String(textAlignRaw)
			: undefined;
	const lwRaw = style?.lineWidth ?? style?.get?.('lineWidth');
	const lineWidth =
		lwRaw != null && lwRaw !== '' && Number.isFinite(Number(lwRaw)) ? Number(lwRaw) : undefined;
	return {textAlign, lineWidth};
}

/**
 * @param {import('../ModelerX6').default} modeler
 * @param {import('@antv/x6').Node[]} nodeCells
 * @returns {boolean}
 */
export function captureBoardClipboard(modeler, nodeCells) {
	if (!modeler || !Array.isArray(nodeCells) || nodeCells.length === 0) {
		modeler._boardClipboard = null;
		return false;
	}

	const oldIds = new Set(nodeCells.map(c => c?.id).filter(Boolean));
	/** @type {Array<Record<string, unknown>>} */
	const nodes = [];
	// Bounding box (graph-local) of copied nodes.
	let minX = Infinity;
	let minY = Infinity;
	let maxX = -Infinity;
	let maxY = -Infinity;

	for (const cell of nodeCells) {
		if (!cell?.id || typeof cell.isNode !== 'function' || !cell.isNode()) continue;
		const ve = modeler._findViewElementById(cell.id);
		if (!ve) continue;
		const type = modeler._getType(ve);
		if (!type || !modeler._isRenderableElement(type)) continue;

		const pos =
			typeof cell.getPosition === 'function'
				? cell.getPosition()
				: {x: ve.x ?? 0, y: ve.y ?? 0};
		const size =
			typeof cell.getSize === 'function'
				? cell.getSize()
				: {width: ve.w ?? 120, height: ve.h ?? 55};
		const style = ve.style;
		const displayName = modeler._getLabel(ve) || modeler._getDefaultLabelForType(type) || type;
		const {textAlign, lineWidth} = captureViewStyleFields(ve);

		const x = Math.round(Number(pos?.x) || 0);
		const y = Math.round(Number(pos?.y) || 0);
		const w = Math.round(Number(size?.width ?? ve.w) || 120);
		const h = Math.round(Number(size?.height ?? ve.h) || 55);
		minX = Math.min(minX, x);
		minY = Math.min(minY, y);
		maxX = Math.max(maxX, x + w);
		maxY = Math.max(maxY, y + h);

		nodes.push({
			oldNodeId: cell.id,
			type,
			name: displayName,
			x,
			y,
			w,
			h,
			fillColor: moddleColorToPatch(style?.fillColor),
			lineColor: moddleColorToPatch(style?.lineColor),
			...(textAlign != null ? {textAlign} : {}),
			...(lineWidth != null ? {lineWidth} : {}),
			/** Для Note/Sticker текст уходит в node.label (styles в БД), без концепта модели. */
			noteLabel:
				type === NOTE || isCanvasOnlyNodeType(type) ? displayName : undefined,
		});
	}

	if (nodes.length === 0) {
		modeler._boardClipboard = null;
		return false;
	}

	const graph = modeler.getGraph();
	/** @type {Array<Record<string, unknown>>} */
	const edges = [];
	for (const edge of graph?.getEdges?.() ?? []) {
		if (!edge || typeof edge.isEdge !== 'function' || !edge.isEdge()) continue;
		const src =
			typeof edge.getSourceCellId === 'function'
				? edge.getSourceCellId()
				: edge.getSource?.()?.cell;
		const tgt =
			typeof edge.getTargetCellId === 'function'
				? edge.getTargetCellId()
				: edge.getTarget?.()?.cell;
		if (!oldIds.has(src) || !oldIds.has(tgt)) continue;
		const ve = modeler._findViewElementById(edge.id);
		if (!ve) continue;
		const type = renderView.getRelationshipType(modeler, ve) || 'Association';
		const accessType = renderView.getRelationshipAccessType(modeler, ve);
		const vertices = typeof edge.getVertices === 'function' ? edge.getVertices() : [];
		const bendpoints = (vertices || []).map(p => ({
			x: Math.round(Number(p.x ?? p.$x ?? 0)),
			y: Math.round(Number(p.y ?? p.$y ?? 0)),
		}));
		edges.push({
			oldConnectionId: edge.id,
			sourceNodeId: src,
			targetNodeId: tgt,
			type,
			accessType: accessType ?? undefined,
			bendpoints,
		});
	}

	const bbox = {
		minX,
		minY,
		maxX,
		maxY,
		centerX: minX + (maxX - minX) / 2,
		centerY: minY + (maxY - minY) / 2,
	};

	modeler._boardClipboard = {version: CLIPBOARD_VERSION, nodes, edges, bbox};
	return true;
}

/**
 * @param {import('../ModelerX6').default} modeler
 * @returns {{ ok: boolean, nodeCount?: number, edgeCount?: number, reason?: string }}
 */
export function pasteBoardClipboard(modeler, options = {}) {
	const clip = modeler?._boardClipboard;
	if (!clip?.nodes?.length) {
		return {ok: false, reason: 'empty'};
	}

	const moddle = modeler._moddle;
	const model = modeler._model;
	const view = modeler._currentView;
	if (!moddle || !model || !view) {
		return {ok: false, reason: 'no-model'};
	}

	const viewIndex = modeler.getActiveViewIndex?.() ?? 0;
	const graph = modeler.getGraph?.();
	let anchorLocal = null;
	const ap = options?.anchorPoint;
	if (ap && typeof ap.x === 'number' && typeof ap.y === 'number') {
		anchorLocal = {x: ap.x, y: ap.y};
	} else {
		const acp = options?.anchorClientPoint;
		if (acp && typeof acp.x === 'number' && typeof acp.y === 'number' && graph) {
			const cx = acp.x;
			const cy = acp.y;
			const p =
				typeof graph.clientToLocal === 'function'
					? graph.clientToLocal(cx, cy)
					: graph.coord?.clientToLocalPoint?.(cx, cy);
			if (p && typeof p.x === 'number' && typeof p.y === 'number') {
				anchorLocal = {x: p.x, y: p.y};
			}
		}
	}

	let dx = PASTE_OFFSET;
	let dy = PASTE_OFFSET;
	if (
		anchorLocal &&
		clip?.bbox &&
		typeof clip.bbox.centerX === 'number' &&
		typeof clip.bbox.centerY === 'number'
	) {
		dx = Math.round(anchorLocal.x - clip.bbox.centerX);
		dy = Math.round(anchorLocal.y - clip.bbox.centerY);
	}

	/** @type {Map<string, string>} */
	const nodeIdMap = new Map();
	/** @type {import('@antv/x6').Node[]} */
	const newCells = [];

	/** @type {Array<Record<string, unknown>>} */
	const patches = [];

	for (const n of clip.nodes) {
		const newNodeId =
			moddle.ids?.nextPrefixed?.('id-', model) ??
			`id-node-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
		const newElemId =
			moddle.ids?.nextPrefixed?.('id-elem-', model) ??
			`id-elem-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
		nodeIdMap.set(n.oldNodeId, newNodeId);

		const canvasOnly = isCanvasOnlyNodeType(n.type);
		/** @type {Record<string, unknown>} */
		const nodePatch = {
			identifier: newNodeId,
			type: n.type,
			name: n.name,
			x: n.x + dx,
			y: n.y + dy,
			w: n.w,
			h: n.h,
		};
		if (!canvasOnly) {
			nodePatch.elementRef = newElemId;
		}
		if (n.noteLabel != null) {
			nodePatch.label = n.noteLabel;
		}
		patches.push({op: 'addNode', viewIndex, node: nodePatch});

		if (n.fillColor || n.lineColor) {
			/** @type {Record<string, unknown>} */
			const styleOp = {
				op: 'updateNode',
				viewIndex,
				identifier: newNodeId,
			};
			if (n.fillColor) styleOp.fillColor = n.fillColor;
			if (n.lineColor) styleOp.lineColor = n.lineColor;
			patches.push(styleOp);
		}

		const textStyles = {};
		if (n.textAlign != null && String(n.textAlign).trim() !== '') {
			textStyles.textAlign = String(n.textAlign);
		}
		if (n.lineWidth != null && Number.isFinite(Number(n.lineWidth))) {
			textStyles.lineWidth = Number(n.lineWidth);
		}
		if (Object.keys(textStyles).length) {
			patches.push({
				op: 'updateNodeStyles',
				viewIndex,
				identifier: newNodeId,
				styles: textStyles,
			});
		}
	}

	for (const e of clip.edges || []) {
		const s = nodeIdMap.get(e.sourceNodeId);
		const t = nodeIdMap.get(e.targetNodeId);
		if (!s || !t) continue;
		const newConnId =
			moddle.ids?.nextPrefixed?.('id-conn-', view) ??
			`id-conn-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
		const bendpoints = (e.bendpoints || []).map(p => ({
			x: (p.x ?? 0) + dx,
			y: (p.y ?? 0) + dy,
		}));
		/** @type {Record<string, unknown>} */
		const conn = {
			identifier: newConnId,
			source: s,
			target: t,
			type: e.type || 'Association',
			bendpoints,
		};
		if (e.accessType != null) conn.accessType = e.accessType;
		patches.push({op: 'addConnection', viewIndex, connection: conn});
	}

	try {
		modeler.applyModelPatch({patches});
	} catch (err) {
		console.warn('[boardCopyPaste] paste failed:', err);
		return {ok: false, reason: 'apply-failed'};
	}

	if (typeof modeler._onPatchCallback === 'function') {
		try {
			modeler._onPatchCallback({patches});
		} catch (_) {}
	}

	if (graph) {
		for (const n of clip.nodes) {
			const nid = nodeIdMap.get(n.oldNodeId);
			const cell = nid ? graph.getCellById?.(nid) : null;
			if (cell && cell.isNode?.()) newCells.push(cell);
		}
		if (newCells.length && typeof graph.resetSelection === 'function') {
			graph.resetSelection(newCells);
		}
	}

	return {ok: true, nodeCount: clip.nodes.length, edgeCount: (clip.edges || []).length};
}
